import i18n from 'i18next'

export const supportEmail = 'support@artem.earth'
export const localStorageInstallationKey = 'installation-creation-state'
export const localStorageProductionRouteKey = 'production-route-creation-state'

export const UnitLabel = new Proxy(
  {},
  {
    get: (_, prop: string) => i18n.t(`common:units.${prop}`) as string
  }
) as Record<string, string>

export enum Unit {
  kgCF4PerTAlPerAE = '(kg CF4/t Al)/(AE-Mins/ cell-day)',
  kgCF4PerTAlPerMv = '(kg CF4/t Al)/mV',
  kgC2F6PerTAlPerMv = '(kg C2F6/t Al)/mV',
  tC2F6PerTAlPerCf4 = 't C2F6/t CF4',
  tCO2ePerTCf4 = 'tCO2e/tCF4',
  tCO2ePerTC2F6 = 'tCO2e/tC2F6',
  tCliPerTCem = 't Cli / t Cem',
  tCO2ePerTCli = 't CO2e/t Cli',
  tAnodePerTAl = 't anode/t Al',
  tCO2ePerMWh = 'tCO₂e/MWh',
  tCO2PerMWh = 'tCO₂/MWh',
  tCO2ePerTJ = 'tCO₂e/TJ',
  tCO2PerTJ = 'tCO₂/TJ',
  tCO2PerT = 'tCO₂/t',
  MWhPerT = 'MWh/t',
  TJpGg = 'TJ/Gg',
  PJpGg = 'PJ/Gg',
  kgpM3 = 'kg/m³',
  GJpT = 'GJ/t',
  mV = 'mV',
  t = 't',
  m3 = 'm³',
  kg = 'kg',
  GJ = 'GJ',
  TJ = 'TJ',
  MWh = 'MWh',
  tAl = 't Al',
  percent = '%',
  tCO2 = 'tCO₂',
  tCO2e = 'tCO₂e',
  wtPerCent = 'wt%',
  tPerTAl = 't/t Al',
  kgPerTAl = 'kg/t Al',
  tCO2ePerT = 'tCO₂e/t',
  tCarbonPerT = 't C/t'
}

export enum CarbonateFormula {
  Calcium = 'CaCO3',
  Magnesium = 'MgCO3',
  Sodium = 'Na2CO3',
  Barium = 'BaCO3',
  Lithium = 'Li2CO3',
  Potassium = 'K2CO3',
  Strontium = 'SrCO3',
  SodiumBi = 'NaHCO3',
  IronII = 'FeCO3'
}

export const CARBONATES = [
  {
    name: 'Calcium Carbonate',
    formula: CarbonateFormula.Calcium,
    emissionFactor: '0.440'
  },
  {
    name: 'Magnesium Carbonate',
    formula: CarbonateFormula.Magnesium,
    emissionFactor: '0.522'
  },
  {
    name: 'Sodium Carbonate',
    formula: CarbonateFormula.Sodium,
    emissionFactor: '0.415'
  },
  {
    name: 'Barium Carbonate',
    formula: CarbonateFormula.Barium,
    emissionFactor: '0.223'
  },
  {
    name: 'Lithium Carbonate',
    formula: CarbonateFormula.Lithium,
    emissionFactor: '0.596'
  },
  {
    name: 'Potassium Carbonate',
    formula: CarbonateFormula.Potassium,
    emissionFactor: '0.318'
  },
  {
    name: 'Strontium Carbonate',
    formula: CarbonateFormula.Strontium,
    emissionFactor: '0.298'
  },
  {
    name: 'Sodium Bicarbonate',
    formula: CarbonateFormula.SodiumBi,
    emissionFactor: '0.524'
  },
  {
    name: 'Iron(II) Carbonate',
    formula: CarbonateFormula.IronII,
    emissionFactor: '0.380'
  }
]

export const CARBONATES_BY_FORMULA = CARBONATES.reduce(
  (acc, carbonate) => {
    acc[carbonate.formula] = carbonate
    return acc
  },
  {} as Record<string, (typeof CARBONATES)[number]>
)

const aluminiumQpds = [
  { code: 'QPD21', required: true },
  { code: 'QPD22', required: true },
  { code: 'QPD23', required: false }
]

const crudeSteelQpds = [
  { code: 'QPD11', required: true },
  { code: 'QPD13', required: true },
  { code: 'QPD14', required: true },
  { code: 'QPD15', required: true },
  { code: 'QPD16', required: true },
  { code: 'QPD17', required: true },
  { code: 'QPD18', required: true }
]

const directReductionQpds = [
  { code: 'QPD10', required: true },
  { code: 'QPD13', required: true },
  { code: 'QPD14', required: true },
  { code: 'QPD15', required: true },
  { code: 'QPD16', required: true }
]
export const PRODUCTION_METHOD_TO_QPD: Record<string, { code: string; required: boolean }[]> = {
  P01: [{ code: 'QPD01', required: true }],
  P02: [], // Does not have any qualifying parameters
  P03: [{ code: 'QPD02', required: true }],
  P04: [], // Does not have any qualifying parameters
  P05: [],
  P08: [],
  P09: [],
  P10: [],
  P11: [],
  P12: [],
  P13: [],
  P14: [],
  P15: [],
  P16: [],
  P17: [{ code: 'QPD04', required: true }],
  P19: [{ code: 'QPD05', required: true }],
  P20: [{ code: 'QPD05', required: true }],
  P21: [{ code: 'QPD05', required: true }],
  P22: [
    { code: 'QPD06', required: false },
    { code: 'QPD07', required: false },
    { code: 'QPD08', required: false },
    { code: 'QPD09', required: false }
  ],
  P23: [{ code: 'QPD03', required: true }],
  P24: [
    { code: 'QPD10', required: true },
    { code: 'QPD13', required: true },
    { code: 'QPD14', required: true },
    { code: 'QPD15', required: true },
    { code: 'QPD16', required: true }
  ],
  P25: [
    { code: 'QPD10', required: true },
    { code: 'QPD13', required: true },
    { code: 'QPD14', required: true },
    { code: 'QPD15', required: true },
    { code: 'QPD16', required: true }
  ],
  P26: [
    { code: 'QPD10', required: true },
    { code: 'QPD13', required: true },
    { code: 'QPD14', required: true },
    { code: 'QPD15', required: true },
    { code: 'QPD16', required: true }
  ],
  P27: [
    { code: 'QPD10', required: true },
    { code: 'QPD13', required: true },
    { code: 'QPD14', required: true },
    { code: 'QPD15', required: true },
    { code: 'QPD16', required: true }
  ],
  P28: [
    { code: 'QPD12', required: true },
    { code: 'QPD13', required: true }
  ],
  P29: [
    { code: 'QPD12', required: true },
    { code: 'QPD13', required: true }
  ],
  P30: [
    { code: 'QPD12', required: true },
    { code: 'QPD13', required: true }
  ],
  P31: [
    { code: 'QPD12', required: true },
    { code: 'QPD13', required: true }
  ],
  P32: directReductionQpds,
  P33: directReductionQpds,
  P34: [
    { code: 'QPD11', required: true },
    { code: 'QPD13', required: true },
    { code: 'QPD14', required: true },
    { code: 'QPD15', required: true },
    { code: 'QPD16', required: true },
    { code: 'QPD18', required: true },
    { code: 'QPD19', required: true },
    { code: 'QPD20', required: false }
  ],
  P35: crudeSteelQpds,
  P36: crudeSteelQpds,
  P37: crudeSteelQpds,
  P38: crudeSteelQpds,
  P39: crudeSteelQpds,
  P40: crudeSteelQpds,
  P41: crudeSteelQpds,
  P42: aluminiumQpds,
  P43: aluminiumQpds,
  P44: aluminiumQpds,
  P45: aluminiumQpds,
  P46: aluminiumQpds,
  P47: aluminiumQpds,
  P48: aluminiumQpds
}

export const INTEGRATED_PRODUCTION_METHODS_COMBINATIONS: Record<
  string,
  {
    codes: string[]
  }[]
> = {
  P24: [
    {
      codes: ['P05', 'P24']
    }
  ],
  P25: [
    {
      codes: ['P05', 'P25']
    }
  ],
  P35: [
    {
      codes: ['P05', 'P24', 'P35']
    },
    {
      codes: ['P05', 'P25', 'P35']
    },
    {
      codes: ['P25', 'P35']
    },
    {
      codes: ['P24', 'P35']
    }
  ],
  P38: [
    {
      codes: ['P05', 'P33', 'P38']
    },
    {
      codes: ['P33', 'P38']
    }
  ],
  P33: [
    {
      codes: ['P05', 'P33']
    }
  ]
}
